import { useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { ReflectionData } from '../components/InternshipDashboard/ReviewPrep/PreparationWizard/ReflectionStep';
import { TalkingPointsData } from '../components/InternshipDashboard/ReviewPrep/PreparationWizard/TalkingPointsStep';
import { TalkingPointsResponse } from './useTalkingPoints';

export interface ReviewPreparation {
  id: string;
  user_id: string;
  focus_id: string;
  reflections: ReflectionData;
  talking_points: TalkingPointsData;
  ai_talking_points?: Record<string, TalkingPointsResponse>;
  metrics_snapshot: {
    overallCoverage: number;
    totalCards: number;
    criticalGaps: string[];
    rubricCoverage?: any[];
  };
  version: number;
  completed_at: string;
  created_at: string;
  updated_at: string;
}

export function useReviewPreparation() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  // Get review preparation for a focus
  const getReviewPreparation = useCallback(async (focusId: string): Promise<ReviewPreparation | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('review_preparations')
        .select('*')
        .eq('user_id', user.id)
        .eq('focus_id', focusId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching review preparation:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching review preparation:', error);
      return null;
    }
  }, [user]);

  // Save or update review preparation
  const saveReviewPreparation = useCallback(async (
    focusId: string,
    reflections: ReflectionData,
    talkingPoints: TalkingPointsData,
    metricsSnapshot: any,
    aiTalkingPoints?: Record<string, TalkingPointsResponse>
  ): Promise<ReviewPreparation | null> => {
    if (!user) return null;

    setLoading(true);
    try {
      // Check if preparation already exists
      const existing = await getReviewPreparation(focusId);

      if (existing) {
        // Update existing preparation
        const { data, error } = await supabase
          .from('review_preparations')
          .update({
            reflections,
            talking_points: talkingPoints,
            ai_talking_points: aiTalkingPoints,
            metrics_snapshot: metricsSnapshot,
            version: existing.version + 1,
            completed_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        
        return data;
      } else {
        // Create new preparation
        const { data, error } = await supabase
          .from('review_preparations')
          .insert({
            user_id: user.id,
            focus_id: focusId,
            reflections,
            talking_points: talkingPoints,
            ai_talking_points: aiTalkingPoints,
            metrics_snapshot: metricsSnapshot
          })
          .select()
          .single();

        if (error) throw error;
        
        return data;
      }
    } catch (error) {
      console.error('Error saving review preparation:', error);
      showToast('Failed to save review preparation', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, getReviewPreparation, showToast]);

  // Delete review preparation
  const deleteReviewPreparation = useCallback(async (focusId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('review_preparations')
        .delete()
        .eq('user_id', user.id)
        .eq('focus_id', focusId);

      if (error) throw error;
      
      showToast('Review preparation deleted', 'success');
      return true;
    } catch (error) {
      console.error('Error deleting review preparation:', error);
      showToast('Failed to delete review preparation', 'error');
      return false;
    }
  }, [user, showToast]);

  return {
    getReviewPreparation,
    saveReviewPreparation,
    deleteReviewPreparation,
    loading
  };
}